import axios from 'axios';
import { apiClient, retryRequest } from './httpClient';
import { environment } from '../environments/environment';

const apiEndpoint = environment.apiBaseUrl + 'storage';

/**
 * Build query string without encoding forward slashes
 */
function buildQueryString(params) {
  if (!params) return '';

  const parts = [];
  for (const key in params) {
    if (params.hasOwnProperty(key) && params[key] !== undefined && params[key] !== null) {
      // Encode the value but keep forward slashes unencoded
      const encodedValue = encodeURIComponent(params[key]).replace(/%2F/g, '/');
      parts.push(`${key}=${encodedValue}`);
    }
  }

  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Get presigned URL from backend
 */
export async function getPresignedUrl(params, options = {}) {
  const query = buildQueryString(params);
  const requestConfig = {
    url: `${apiEndpoint}/presigned-url${query}`,
    method: 'GET',
    ...options
  };

  try {
    const response = await retryRequest(() => apiClient(requestConfig));
    return response.data;
  } catch (error) {
    const err = new Error(error.data || 'Failed to get presigned URL');
    err.status = error.status || 500;
    err.endpoint = apiEndpoint;
    err.method = 'GET';
    throw err;
  }
}

/**
 * Upload file to AWS S3 using presigned URL
 */
export async function uploadFileToAwsS3(presignedUrl, contentType, file, onProgress) {
  try {
    const response = await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': contentType,
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });

    return response;
  } catch (error) {
    const err = new Error(error.data || 'Failed to upload file to S3');
    err.status = error.response?.status || 500;
    throw err;
  }
}

/**
 * Delete file from AWS S3 using presigned URL
 */
export async function deleteFileFromAwsS3(presignedUrl) {
  try {
    const response = await axios.delete(presignedUrl);

    return response.status === 204 ? { success: true } : response.data;
  } catch (error) {
    const err = new Error(error.data || 'Failed to delete file from S3');
    err.status = error.response?.status || 500;
    err.details = error.response?.data;
    throw err;
  }
}

/**
 * Check if file exists in S3
 */
export async function checkFileExists(params, options = {}) {
  const query = buildQueryString(params);
  const requestConfig = {
    url: `${apiEndpoint}/exists${query}`,
    method: 'GET',
    ...options
  };

  try {
    const response = await retryRequest(() => apiClient(requestConfig));
    return response.data;
  } catch (error) {
    const err = new Error(error.data || 'Failed to check file existence');
    err.status = error.status || 500;
    err.endpoint = apiEndpoint;
    err.method = 'GET';
    throw err;
  }
}

/**
 * Download file using presigned URL
 */
export async function downloadFile(presignedUrl, fileName) {
  try {
    const response = await axios.get(presignedUrl, {
      responseType: 'blob',
    });

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return response;
  } catch (error) {
    const err = new Error(error.data || 'Failed to download file');
    err.status = error.response?.status || 500;
    throw err;
  }
}
